import axios from 'axios';
import { storeAction } from '../storeActions.constant';
import { storeGetters } from '../storeGetters.constant';
import { storeMutation } from '../storeMutations.constant';
import { getBasicHeaders } from '../../common/utils/utils';
import { wrapAction } from '../../common/utils/actionLifecycle';
import { getMemberProfileFromSpeakerProfile } from '../classes/memberProfile/memberProfile';
import { addAllOption } from '../../components/MyAccount/myaccount.constants';
import { datadogRum } from "@datadog/browser-rum";

const myAccountState = {
    baseURL: process.env.VUE_APP_BASE_URL,
    speakerBaseURL: process.env.VUE_APP_SPEAKER_BASE_URL,
    profileInformation: null,
    usernameResponse: '',
    notificationInformation: '',
    visibilityInformation: '',
    passwordResponse: '',
    emailInformation: '',
    emailPreferencesResponse: '',
    profileImage: null,
    fallBackLoggedInProfileImageUrl: '',
    renewalStatus: '',
};

const myAccountMutations = {
    [storeMutation.setprofileInformation]: (state, data) => {
        state.profileInformation = data;
        state.profileImage = {
            originalImage: data.profileImageUrl,
            fallBackImage: data.fallbackProfileImageUrl,
            userName: data.name,
        };
        state.fallBackLoggedInProfileImageUrl = data.fallBackProfileImageUrl;
    },
    [storeMutation.updateProfileImageUrl]: (state, profileImageUrl) => {
        state.profileImage = {
            ...state.profileImage,
            originalImage: profileImageUrl,
        };
        state.profileInformation = {
            ...state.profileInformation,
            profileImageUrl,
        };
    },
    [storeMutation.setProfileInfoUpdatedFromSpeaker]: (state, data) => {
        if (!data.isSpeakerView) {
            state.profileInformation = {
                ...state.profileInformation,
                preferredName: data.speakerProfile.preferredName,
                firstName: data.speakerProfile.firstName,
                lastName: data.speakerProfile.familyName,
            };
        } else {
            state.profileInformation = getMemberProfileFromSpeakerProfile(data.speakerProfile);
        }
        state.profileImage = {
            originalImage: data.speakerProfile.profileImageUrl,
            fallBackImage: data.speakerProfile.fallbackProfileImageUrl,
            userName:
                data.speakerProfile.preferredName === null
                    ? `${data.speakerProfile.firstName} ${data.speakerProfile.familyName}`
                    : `${data.speakerProfile.preferredName} ${data.speakerProfile.familyName}`,
        };
    },
    [storeMutation.setProfileInformationUsername]: (state, data) => {
        if (!state.profileInformation) state.profileInformation = {};
        state.profileInformation.webLoginIdhistorical = data;
    },
    [storeMutation.setUsernameResponse]: (state, data) => {
        state.usernameResponse = data;
    },
    [storeMutation.setnotification]: (state, data) => {
        state.notificationInformation = data;
    },
    [storeMutation.setVisibilityInformation]: (state, data) => {
        state.visibilityInformation = data;
    },
    [storeMutation.setPasswordResponse]: (state, data) => {
        state.passwordResponse = data;
    },
    [storeMutation.setEmailPreferences]: (state, data) => {
        state.emailInformation = data;
    },
    [storeMutation.setEmailPreferencesResponse]: (state, data) => {
        state.emailPreferencesResponse = data;
    },
    [storeMutation.setRenewalStatus]: (state, data) => {
        state.renewalStatus = data;
    },
};

const myAccountActions = {
    [storeAction.getProfileInformation]: wrapAction(storeAction.getProfileInformation, ({
            state,
            commit,
            rootState,
            dispatch,
        }, currentUserGuid) => {
        return new Promise((resolve) => {
            if (rootState.auth.isSpeakerView) {
                resolve(dispatch(
                    `speaker/${storeAction.getSpeakerProfile}`,
                    {
                        Id: rootState.auth.currentUserId,
                        Type: 'member',
                        ApprovalStatus: 'A',
                    },
                    { root: true },
                ));
            } else {
                resolve(axios({
                    url: `${state.baseURL}profiles/api/persons/${currentUserGuid}`,
                    headers: getBasicHeaders(),
                    method: 'GET',
                }));
            }
        })
            .then((response) => {
                commit(
                    storeMutation.setprofileInformation,
                    rootState.auth.isSpeakerView ? getMemberProfileFromSpeakerProfile(response.data) : response.data,
                );
                commit(`ypoConciergeService/${storeMutation.setYpoConciergeUser}`, response.data, { root: true });
                commit(`manageLoginSecurity/${storeMutation.setUserInfo}`, response.data, { root: true });
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    }),
    [storeAction.setDataDogUserInfo]: ({ state }) => {
        const { profileInformation } = state;
        if (profileInformation && profileInformation.id) {
            datadogRum.setUser({
                id: profileInformation.id,
                name: profileInformation.name,
            });
        }
    },
    [storeAction.changeUserName]: ({ state, commit }, newUsername) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/${
                state.profileInformation.webLoginIdhistorical
            }/person/${newUsername}`,
            headers: getBasicHeaders(),
            method: 'POST',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setUsernameResponse, err.response.data);
                console.log(err);
                throw err.response.data;
            });
    },
    [storeAction.getNotification]: wrapAction(storeAction.getNotification, ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}profiles/api/privacy-settings/get-notifications`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setnotification, response.data);
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    }),
    [storeAction.saveNotification]: wrapAction(storeAction.saveNotification, ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}profiles/api/privacy-settings/save-notifications`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                commit(storeMutation.setnotification, response.data);
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err();
            });
    }),
    [storeAction.getVisibilityInformation]: ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}profiles/api/privacy-settings/get-visibility-settings`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setVisibilityInformation, response.data);
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
    [storeAction.saveVisibilityPreferences]: ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}profiles/api/privacy-settings/save-visibility-settings`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                commit(storeMutation.setVisibilityInformation, response.data);
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    },
    [storeAction.changePassword]: ({ state, commit }, requestBody) => {
        return axios({
            url: `${state.baseURL}identities/api/identities/${
                state.profileInformation.webLoginIdhistorical
            }/updatePassword`,
            headers: getBasicHeaders(),
            data: requestBody,
            method: 'POST',
        })
            .then((response) => {
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setPasswordResponse, err.response.data);
                console.log(err);
                throw err.response.data;
            });
    },
    [storeAction.getEmailPreferences]: wrapAction(storeAction.getEmailPreferences, ({ state, commit }) => {
        return axios({
            url: `${state.baseURL}profiles/api/privacy-settings/get-email-settings`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                const updatedPreferences = addAllOption(response.data.communicationPreferences);
                commit(storeMutation.setEmailPreferences, { ...response.data, communicationPreferences: updatedPreferences });
                return response;
            })
            .catch((err) => {
                console.log(err);
                throw err;
            });
    }),
    [storeAction.saveEmailPreferences]: wrapAction(
        storeAction.saveEmailPreferences,
        ({ state, commit }, requestBody) => {
            return axios({
                url: `${state.baseURL}profiles/api/privacy-settings/save-email-settings`,
                headers: getBasicHeaders(),
                data: requestBody,
                method: 'POST',
            })
                .then((response) => {
                    commit(storeMutation.setEmailPreferences, response.data);
                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setEmailPreferencesResponse, err.response.data);
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getRenewalStatus]: wrapAction(storeAction.getRenewalStatus, ({ state, commit }, userId) => {
        return axios({
            url: `${state.baseURL}profiles/api/persons/${userId}/renewal-status`,
            headers: getBasicHeaders(),
            method: 'GET',
        })
            .then((response) => {
                commit(storeMutation.setRenewalStatus, response.data);
                return response;
            })
            .catch((err) => {
                commit(storeMutation.setRenewalStatus, '');
                console.log(err);
                throw err;
            });
    }),
};

const myAccountGetters = {
    [storeGetters.profileInformation]: state => state.profileInformation,
    [storeGetters.usernameResponse]: state => state.usernameResponse,
    [storeGetters.notificationInformation]: state => state.notificationInformation,
    [storeGetters.visibilityInformation]: state => state.visibilityInformation,
    [storeGetters.passwordResponse]: state => state.passwordResponse,
    [storeGetters.emailInformation]: state => state.emailInformation,
    [storeGetters.emailPreferencesResponse]: state => state.emailPreferencesResponse,
    [storeGetters.profileImage]: state => state.profileImage,
    [storeGetters.fallBackLoggedInProfileImageUrl]: state => state.fallBackLoggedInProfileImageUrl,
    [storeGetters.renewalStatus]: state => state.renewalStatus,
};

export default {
    namespaced: true,
    state: {
        initialState: myAccountState,
        ...myAccountState,
    },
    mutations: { ...myAccountMutations },
    actions: { ...myAccountActions },
    getters: { ...myAccountGetters },
};
